import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, MessageCircle, ShoppingBag, ArrowLeft, Send, Clock, Share2, Link2, Twitter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { UserBadges } from '../components/UserBadges';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHARE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/share/look`;

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTimeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
};

const CommentItem = ({ comment }) => (
  <div className="flex gap-3 py-4 border-b border-white/5 last:border-0" data-testid={`comment-${comment.id}`}>
    <Link to={`/profile/${comment.username}`} className="shrink-0">
      <div className="w-8 h-8 rounded-full bg-[#C41E3A]/80 flex items-center justify-center">
        <span className="text-xs font-medium text-white">{comment.username[0].toUpperCase()}</span>
      </div>
    </Link>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 mb-1">
        <Link to={`/profile/${comment.username}`} className="text-sm text-white font-medium hover:text-[#C41E3A] transition-colors">
          @{comment.username}
        </Link>
        <UserBadges role={comment.user_role} isVerified={comment.user_verified} size="xs" showRole={false} />
        <span className="text-xs text-gray-600 ml-auto shrink-0">{formatTimeAgo(comment.created_at)}</span>
      </div>
      <p className="text-sm text-gray-300 leading-relaxed">{comment.content}</p>
    </div>
  </div>
);

const ShareBar = ({ lookId, title }) => {
  const shareUrl = `${SHARE_BASE}/${lookId}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied!');
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      toast.success('Share link copied!');
    }
  };

  const shareTwitter = () => {
    const text = encodeURIComponent(`${title} on Meet Mr. Fashion`);
    const url = encodeURIComponent(shareUrl);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'noopener,noreferrer');
  };

  const shareLinkedIn = () => {
    const url = encodeURIComponent(shareUrl);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex items-center gap-3 mb-6" data-testid="share-bar">
      <span className="text-xs uppercase tracking-wider text-gray-500 mr-1">Share</span>
      <button
        onClick={copyLink}
        className="flex items-center gap-1.5 px-3 py-2 border border-white/10 rounded-sm text-gray-400 hover:text-white hover:border-white/20 transition-colors text-xs"
        data-testid="share-copy-link"
      >
        <Link2 className="w-3.5 h-3.5" />
        Copy Link
      </button>
      <button
        onClick={shareTwitter}
        className="flex items-center gap-1.5 px-3 py-2 border border-white/10 rounded-sm text-gray-400 hover:text-white hover:border-white/20 transition-colors text-xs"
        data-testid="share-twitter"
      >
        <Twitter className="w-3.5 h-3.5" />
        Twitter
      </button>
      <button
        onClick={shareLinkedIn}
        className="flex items-center gap-1.5 px-3 py-2 border border-white/10 rounded-sm text-gray-400 hover:text-white hover:border-white/20 transition-colors text-xs"
        data-testid="share-linkedin"
      >
        <Share2 className="w-3.5 h-3.5" />
        LinkedIn
      </button>
    </div>
  );
};

const LookDetailPage = () => {
  const { lookId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [look, setLook] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);

  const fetchLook = useCallback(async () => {
    try {
      const [lookRes, commentsRes] = await Promise.all([
        axios.get(`${API}/looks/${lookId}`),
        axios.get(`${API}/looks/${lookId}/comments`)
      ]);
      setLook(lookRes.data);
      setComments(commentsRes.data);
      setIsLiked(lookRes.data.liked_by_user);
      setLikesCount(lookRes.data.likes_count);
    } catch {
      toast.error('Look not found');
    } finally {
      setLoading(false);
    }
  }, [lookId]);

  useEffect(() => { fetchLook(); }, [fetchLook]);

  const handleLike = async () => {
    if (!isAuthenticated) { toast.error('Sign in to like looks'); return; }
    try {
      const res = await axios.post(`${API}/looks/${lookId}/like`);
      setIsLiked(res.data.liked);
      setLikesCount(prev => res.data.liked ? prev + 1 : prev - 1);
    } catch {
      toast.error('Failed to update like');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/looks/${lookId}/comments`, { content: commentText });
      setComments(prev => [res.data, ...prev]);
      setCommentText('');
      toast.success('Comment posted');
    } catch {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShopClick = async () => {
    if (!look?.affiliate_url) return;
    window.open(look.affiliate_url, '_blank', 'noopener,noreferrer');
    try {
      await axios.post(`${API}/affiliate/click/${lookId}`);
    } catch (err) {
      console.error('Affiliate click tracking failed:', err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="skeleton aspect-[4/5] rounded-sm" />
            <div className="space-y-4">
              <div className="skeleton h-8 w-3/4 rounded" />
              <div className="skeleton h-4 w-1/2 rounded" />
              <div className="skeleton h-32 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!look) {
    return (
      <div className="min-h-screen pt-20 bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-4">Look not found</p>
          <Link to="/feed"><Button className="btn-luxury btn-luxury-secondary">Back to Feed</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#0a0a0a]" data-testid="look-detail-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        {/* Back */}
        <Link to="/feed" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 text-sm" data-testid="back-to-feed">
          <ArrowLeft className="w-4 h-4" />
          Back to Feed
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[4/5] bg-[#1c1c1c] overflow-hidden rounded-sm">
              <img src={look.image_url} alt={look.title} className="w-full h-full object-cover" data-testid="look-image" />
            </div>
          </div>

          {/* Details */}
          <div className="flex flex-col">
            {/* Brand Tags */}
            {look.brand_tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {look.brand_tags.map(tag => (
                  <Link key={tag} to={`/brands/${tag.toLowerCase().replace(/ /g, '-').replace(/è/g, 'e')}`} className="tag-pill text-[10px] hover:bg-white/20 transition-colors">
                    {tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-serif italic text-white mb-3" data-testid="look-title">
              {look.title}
            </h1>

            {/* Author */}
            <div className="flex items-center gap-3 mb-6">
              <Link to={`/profile/${look.username}`} className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-[#C41E3A] flex items-center justify-center">
                  {look.user_avatar ? (
                    <img src={look.user_avatar} alt={look.username} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-sm font-medium text-white">{look.username[0].toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-white text-sm font-medium hover:text-[#C41E3A] transition-colors">@{look.username}</span>
                    <UserBadges role={look.user_role} isVerified={look.user_verified} size="xs" showRole={false} />
                  </div>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(look.created_at)}
                  </span>
                </div>
              </Link>
            </div>

            {/* Description */}
            {look.description && (
              <div className="mb-6 pb-6 border-b border-white/5">
                <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">{look.description}</p>
              </div>
            )}

            {/* Tags */}
            {look.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {look.tags.map(tag => (
                  <span key={tag} className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded-sm">#{tag}</span>
                ))}
              </div>
            )}

            {/* Actions Bar */}
            <div className="flex items-center gap-6 py-4 border-y border-white/5 mb-6">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 text-sm transition-colors ${isLiked ? 'text-[#C41E3A]' : 'text-gray-400 hover:text-white'}`}
                data-testid="like-btn"
              >
                <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                <span className="font-medium">{likesCount}</span>
              </button>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <MessageCircle className="w-5 h-5" />
                <span>{comments.length}</span>
              </div>
              {look.affiliate_url && (
                <button
                  onClick={handleShopClick}
                  className="flex items-center gap-2 text-[#C41E3A] hover:text-white transition-colors text-sm ml-auto"
                  data-testid="shop-look-btn"
                >
                  <ShoppingBag className="w-5 h-5" />
                  <span className="text-xs uppercase tracking-wider font-medium">Shop This Look</span>
                </button>
              )}
            </div>

            {/* Share Bar */}
            <ShareBar lookId={lookId} title={look.title} />

            {/* Comment Form */}
            {isAuthenticated ? (
              <form onSubmit={handleComment} className="flex gap-3 mb-6" data-testid="comment-form">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 bg-[#1c1c1c] border border-white/10 text-white text-sm px-4 py-3 placeholder:text-gray-600 focus:outline-none focus:border-[#C41E3A]/50 transition-colors rounded-sm"
                  data-testid="comment-input"
                />
                <Button
                  type="submit"
                  disabled={submitting || !commentText.trim()}
                  className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 disabled:opacity-50"
                  data-testid="comment-submit"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            ) : (
              <Link to="/auth" className="block mb-6">
                <div className="text-center py-4 border border-white/10 rounded-sm bg-[#1c1c1c] hover:border-[#C41E3A]/30 transition-colors">
                  <p className="text-gray-400 text-sm">Sign in to leave a comment</p>
                </div>
              </Link>
            )}

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto" data-testid="comments-section">
              <h3 className="text-xs uppercase tracking-wider text-gray-400 mb-4">
                Comments ({comments.length})
              </h3>
              {comments.length > 0 ? (
                <div>
                  {comments.map(comment => (
                    <CommentItem key={comment.id} comment={comment} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-sm py-4">No comments yet. Be the first!</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LookDetailPage;
